import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { booleanAttribute, Component, ElementRef, forwardRef, inject, input, signal, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MuiButtonAppearance, MuiButtonModule, MuiButtonSize } from '../../button';
import { MuiDropzone } from '../../dropzone';
import { MuiI18nProvider } from '../../i18n';
import { MuiIconModule } from '../../icon';


export type MuiFileInputValue = File | File[] | null | undefined;
export type MuiFileInputAppearance = 'button' | 'dropzone';
export type MuiFileInputMode = 'replace' | 'append';

type OnChangeFn = (value: MuiFileInputValue) => void;
type OnTouchedFn = () => void;

@Component({
  selector: 'mui-file-input',
  standalone: true,
  imports: [
    MuiDropzone,
    MuiIconModule,
    MuiButtonModule,
    NgTemplateOutlet,
    AsyncPipe
  ],
  templateUrl: './mui-file-input.component.html',
  styleUrl: './mui-file-input.component.scss',
  exportAs: 'muiFileInput',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MuiFileInput),
      multi: true
    }
  ]
})
export class MuiFileInput implements ControlValueAccessor {
  readonly accept = input<string>('.p3p');
  readonly acceptedFormatsLabel = input<string>();
  readonly allowDuplicates = input(false, { transform: booleanAttribute });
  readonly appearance = input<MuiFileInputAppearance | string>('button');
  readonly buttonSize = input<MuiButtonSize | string>('small');
  readonly buttonAppearance = input<MuiButtonAppearance, unknown>('primary', {
    transform: (val) => val as MuiButtonAppearance
  });
  readonly buttonLabel = input<string>('');
  readonly hideButton = input(false, { transform: booleanAttribute });
  readonly dropzoneLabel = input<string>('');
  readonly mode = input<MuiFileInputMode>('replace');
  readonly multiple = input(false, { transform: booleanAttribute });
  readonly disabled = signal(false);

  protected readonly translations$ = inject(MuiI18nProvider).fileInput();

  @ViewChild('fileInput', { static: true })
  private readonly _fileInput!: ElementRef<HTMLInputElement>;
  private _model: Exclude<MuiFileInputValue, File> = null;
  private _onChange: OnChangeFn = () => { /* no-op */ };
  private _onTouched: OnTouchedFn = () => { /* no-op */ };

  writeValue(value: MuiFileInputValue): void {
    this._model = value instanceof File ? [value] : value;
  }

  registerOnChange(fn: OnChangeFn): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: OnTouchedFn): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  clearSelection(): void {
    this._model = null;
    this._onChange(null);
  }

  open(): void {
    if (this.disabled()) {
      return;
    }
    this._fileInput.nativeElement.click();
  }

  protected async onFileSelected(event: Event): Promise<void> {
    const inp = event.target as HTMLInputElement;
    const newFiles = Array.from(inp.files ? inp.files : []);
    this.onNewFiles(newFiles);
  }

  protected onFileInputClick(event: Event): void {
    /* Resetting the input value makes it possible to select the same file again, e.g. after it has been removed from
     the list */
    event.stopPropagation();
    (event.target as HTMLInputElement).value = '';
  }

  protected onNewFiles(files: File[]): void {
    if (this.mode() === 'append' && this._model) {
      const existingHashes = this._model.map(oldFile => this.createFileHash(oldFile));
      const filesToAdd = this.allowDuplicates() ? files : files.filter(newFile => {
        return !existingHashes.includes(this.createFileHash(newFile));
      });
      this._model = this._model.concat(filesToAdd);
    } else {
      this._model = files;
    }

    this._onTouched();
    this._onChange(this._model.length > 1 && !this.multiple() ? this._model[0] : this._model);
  }

  /**
   * Create a simple hash for the file based on its name, last modified date and size. Real hash take too long for this
   * purpose, and we can live with a simple solution here for now.
   */
  private createFileHash(file: File): string {
    return `${file.name}-${file.lastModified}-${file.size}`;
  }
}
